import classNames from 'classnames';
import React, { useMemo } from 'react';
import { Checkbox } from '../../../formControls/Checkbox/Checkbox';
import { DatePicker } from '../../../formControls/DatePicker/DatePicker';
import { Select } from '../../../formControls/Select/Select';
import { DEFAULT_TIME_FORMAT, TIMEZONES } from '../../../../constants/constants';
import { DateHelper } from '../../../../utils';
import { ThirdStepTooltip } from './ThirdStepTooltip';

import styles from './index.module.css';
import {
  DATE_TIME_FIELD_LABELS,
  DATE_TIME_LABEL_VARIANT,
  getQuickScheduleDatePickerPopupContainer,
  getReturnDateFieldLabel,
  getReturnEarliestLabel,
  isScheduleRideLayout,
  QUICK_SCHEDULE_DATE_PICKER_POPUP_CLASS,
} from './stepThreeDateTimeConstants';

export const ReturnDateTimeSection = ({
  pickUpDate,
  returnTimeDate,
  maxPickUpWindowOfTime,
  handleReturnTime,
  handleReturnTimeOnSelect,
  handleReturnLowerLimitOnSelect,
  setFieldValue,
  disabledDatesForReturnTime,
  disabledDateTime,
  handleUseWindowOfTime,
  handleChangeTZ,
  values,
  disabledValues,
  returnTimeLowerLimit,
  handleReturnTimeLowerLimit,
  disabledDateForReturnHigherLimit,
  disabledDateTimeForReturnHigherLimit,
  minimumRoundTrip,
  isUberRide = false,
  hideFlexibleCheckbox = false,
  labelVariant = DATE_TIME_LABEL_VARIANT.DEFAULT,
  fieldInputClassName,
  selectFieldClassName,
}) => {
  const datePickerClass = fieldInputClassName || undefined;
  const selectClass = selectFieldClassName || fieldInputClassName || undefined;
  const minAvailableReturnDate = useMemo(() => {
    if (!pickUpDate || !DateHelper.isValidDateTime(pickUpDate)) {
      return null;
    }
    const minDate = DateHelper.addDates(pickUpDate, minimumRoundTrip);
    return DateHelper.isValidDateTime(minDate) ? minDate : null;
  }, [pickUpDate, minimumRoundTrip]);

  const diabledDatesForReturnField = useMemo(
    () =>
      values?.isReturnDateFlexible
        ? disabledDateForReturnHigherLimit(returnTimeLowerLimit)
        : disabledDatesForReturnTime(pickUpDate),
    [
      values?.isReturnDateFlexible,
      disabledDateForReturnHigherLimit,
      disabledDatesForReturnTime,
      returnTimeLowerLimit,
      pickUpDate,
    ],
  );

  const disabledDateTImeForReturnField = useMemo(
    () =>
      values?.isReturnDateFlexible
        ? disabledDateTimeForReturnHigherLimit
        : disabledDateTime(minAvailableReturnDate, returnTimeDate),
    [
      values?.isReturnDateFlexible,
      disabledDateTimeForReturnHigherLimit,
      disabledDateTime,
      minAvailableReturnDate,
      returnTimeDate,
    ],
  );

  const returnDateFieldDisabled = useMemo(
    () =>
      !values?.returnTime?.timezone ||
      !pickUpDate ||
      !DateHelper.isValidDateTime(pickUpDate) ||
      (values?.isReturnDateFlexible && !returnTimeLowerLimit) ||
      disabledValues?.returnTime,
    [
      values?.returnTime?.timezone,
      pickUpDate,
      values?.isReturnDateFlexible,
      returnTimeLowerLimit,
      disabledValues?.returnTime,
    ],
  );

  const scheduleRideLayout = isScheduleRideLayout(labelVariant);

  const datePickerPopupClassName = scheduleRideLayout
    ? QUICK_SCHEDULE_DATE_PICKER_POPUP_CLASS
    : undefined;

  const datePickerGetPopupContainer = scheduleRideLayout
    ? getQuickScheduleDatePickerPopupContainer
    : (trigger) => trigger;

  const returnDateFieldLabel = useMemo(
    () =>
      getReturnDateFieldLabel({
        labelVariant,
        isReturnDateFlexible: values?.isReturnDateFlexible,
      }),
    [values?.isReturnDateFlexible, labelVariant],
  );

  const returnEarliestLabel = useMemo(
    () => getReturnEarliestLabel(labelVariant),
    [labelVariant],
  );

  const returnTimeDefaultDateTime = useMemo(() => {
    if (minAvailableReturnDate) return minAvailableReturnDate;
    const fallback = new Date();
    fallback.setHours(0, 0, 0, 0);
    return fallback;
  }, [minAvailableReturnDate]);

  const latestReturnDefaultDateTime = useMemo(() => {
    if (
      values?.isReturnDateFlexible &&
      returnTimeLowerLimit &&
      DateHelper.isValidDateTime(returnTimeLowerLimit)
    ) {
      return returnTimeLowerLimit;
    }
    return returnTimeDefaultDateTime;
  }, [values?.isReturnDateFlexible, returnTimeLowerLimit, returnTimeDefaultDateTime]);

  const schedulePickerDefaults =
    scheduleRideLayout || handleReturnTimeOnSelect
      ? {
          alignPanelToDefault: scheduleRideLayout,
          hideDisabledOptions: false,
          needConfirm: true,
        }
      : {};

  const handleReturnMainDateSelect = (date, setFieldValue, values) => {
    console.log('handleReturnMainDateSelect setFieldValue, values', setFieldValue, values);
    if (
      !date ||
      !DateHelper.isValidDateTime(date) ||
      !handleReturnTimeOnSelect ||
      !pickUpDate ||
      !DateHelper.isValidDateTime(pickUpDate)
    ) {
      return;
    }
    handleReturnTimeOnSelect({ value: date, setFieldValue, values });
  };

  const handleReturnEarliestDateSelect = (date, setFieldValue, values) => {
    if (
      !date ||
      !DateHelper.isValidDateTime(date) ||
      !handleReturnLowerLimitOnSelect ||
      !pickUpDate ||
      !DateHelper.isValidDateTime(pickUpDate)
    ) {
      return;
    }
    handleReturnLowerLimitOnSelect({ value: date, setFieldValue, values });
  };

  return values?.isRoundtrip && !disabledValues?.returnTime ? (
    <>
      {!hideFlexibleCheckbox && (
      <div className={styles.windowOfTime}>
        <label className={styles.useWindowOfTime}>
          <Checkbox
            name='isReturnDateFlexible'
            checked={values?.isReturnDateFlexible}
            onChange={handleUseWindowOfTime}
            disabled={disabledValues?.isReturnDateFlexible}
            whenFieldDisabledShowTooltip={Boolean(disabledValues?.isReturnDateFlexible)}
          />
          <span className={styles.checkboxLabelText}>
            {DATE_TIME_FIELD_LABELS.FLEXIBLE_PICKUP_CHECKBOX}
          </span>
        </label>
        <ThirdStepTooltip range={maxPickUpWindowOfTime} useFlexibleHoursMessage />
      </div>
      )}
      <div
        className={classNames(styles.dateAndTime, {
          qsScheduleDateAndTime: scheduleRideLayout,
        })}
      >
        <label
          className={classNames({
            [styles.timezone]: !scheduleRideLayout,
            qsScheduleTimezone: scheduleRideLayout,
          })}
        >
          <span className={styles.required}>{DATE_TIME_FIELD_LABELS.TIMEZONE}</span>
          <div className={styles.timezoneSection}>
            <Select
              name='returnTime.timezone'
              options={TIMEZONES}
              onChange={handleChangeTZ}
              placeholder={'Pick one'}
              disabled={disabledValues?.pickUpDate}
              myClassName={selectClass}
              whenFieldDisabledShowTooltip={Boolean(disabledValues?.pickUpDate)}
            />
          </div>
        </label>

        <div
          className={classNames(styles.range, {
            qsScheduleRange: scheduleRideLayout,
          })}
        >
          {values?.isReturnDateFlexible && (
            <label
              className={classNames(styles.startDate, {
                qsScheduleStartDate: scheduleRideLayout,
              })}
            >
              <span className={styles.required}>{returnEarliestLabel}</span>
              <DatePicker
                name='returnTimeLowerLimit.datetime'
                myClassName={datePickerClass || styles.datepicker}
                onChange={handleReturnTimeLowerLimit}
                format={DEFAULT_TIME_FORMAT}
                value={
                  returnTimeLowerLimit && DateHelper.isValidDateTime(returnTimeLowerLimit)
                    ? returnTimeLowerLimit
                    : ''
                }
                showTime={{ defaultValue: returnTimeDefaultDateTime }}
                showNow={false}
                use12Hours
                defaultPickerValue={returnTimeDefaultDateTime}
                disabledDate={disabledDatesForReturnTime(pickUpDate) }
                disabledTime={disabledDateTime(minAvailableReturnDate, returnTimeLowerLimit)}
                disabled={!values?.returnTime?.timezone || !pickUpDate || disabledValues?.returnTime}
                whenFieldDisabledShowTooltip={Boolean(disabledValues?.returnTime)}
                inputReadOnly
                allowClear
                popupRootClassName={datePickerPopupClassName}
                getPopupContainer={datePickerGetPopupContainer}
                onSelect={handleReturnEarliestDateSelect}
                {...schedulePickerDefaults}
              />
            </label>
          )}
          <label
            className={classNames({
              [styles.endDate]: values?.isReturnDateFlexible,
              [styles.datepicker]: !values?.isReturnDateFlexible,
              qsScheduleEndDate:
                scheduleRideLayout && values?.isReturnDateFlexible,
              qsScheduleDatepicker:
                scheduleRideLayout && !values?.isReturnDateFlexible,
            })}
          >
            <span className={styles.required}>{returnDateFieldLabel}</span>
            <DatePicker
              name='returnTime.datetime'
              myClassName={datePickerClass || styles.datepicker}
              onChange={handleReturnTime}
              format={DEFAULT_TIME_FORMAT}
              value={
                returnTimeDate && DateHelper.isValidDateTime(returnTimeDate) ? returnTimeDate : ''
              }
              showTime={{ defaultValue: latestReturnDefaultDateTime }}
              showNow={false}
              use12Hours
              defaultPickerValue={latestReturnDefaultDateTime}
              disabledDate={diabledDatesForReturnField}
              disabledTime={disabledDateTImeForReturnField}
              disabled={returnDateFieldDisabled}
              whenFieldDisabledShowTooltip={Boolean(disabledValues?.returnTime)}
              inputReadOnly
              allowClear
              popupRootClassName={datePickerPopupClassName}
              getPopupContainer={datePickerGetPopupContainer}
              onSelect={handleReturnMainDateSelect}
              {...schedulePickerDefaults}
            />
          </label>
        </div>
      </div>
    </>
  ) : null;
};
